import './App.scss';

import React, { useState, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import RequirePricing from '@/components/RequirePricing';
import RequireSignup from '@/components/RequireSignup';
import Login from '@/routes/Login';
import initializeLDProvider from '@/utils/launchdarkly';

// Lazy load routes
const SignUp = React.lazy(() => import('./routes/SignUp'));
const SmsVerification = React.lazy(() => import('./routes/SmsVerification'));
const Checkout = React.lazy(() => import('./routes/Checkout'));
const CreatePassword = React.lazy(() => import('./routes/CreatePassword'));

// Keep legacy /v2 links working while / is the canonical login route.
const LegacyV2Redirect = () => {
  const location = useLocation();

  return (
    <Navigate
      to={{ pathname: '/', search: location.search, hash: location.hash }}
      replace
    />
  );
};

/**
 * LaunchDarkly provider wrapper component
 * Handles async initialization of LaunchDarkly provider with timeout fallback
 * If LDProvider takes more than 3 seconds, renders app without provider
 * Once LDProvider resolves, switches to use the provider
 * @param children - Child components to render within the provider
 */
const LDProviderWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [LDProvider, setLDProvider] = useState<React.ComponentType<{ children: React.ReactNode }> | null>(null);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    let isTimedOut = false;

    // Set 3-second timeout to ensure app loads even if LD is slow
    const timeout = setTimeout(() => {
      isTimedOut = true;
      setShouldRender(true);
      // If LD hasn't loaded yet, use Fragment as temporary fallback
      setLDProvider((current: React.ComponentType<{ children: React.ReactNode }> | null) =>
        current || (React.Fragment as React.ComponentType<{ children: React.ReactNode }>)
      );
    }, 3000);

    const initLD = async () => {
      try {
        const provider = await initializeLDProvider();
        setLDProvider(() => provider);
        if (!isTimedOut) {
          setShouldRender(true);
        }
      } catch (err) {
        console.error('Error initializing LaunchDarkly provider', err);
        // Render children without provider if initialization fails
        setLDProvider(() => React.Fragment as React.ComponentType<{ children: React.ReactNode }>);
        if (!isTimedOut) {
          setShouldRender(true);
        }
      }
    };

    initLD();

    return () => clearTimeout(timeout);
  }, []);

  // Don't render until either timeout expires or LDProvider loads
  if (!shouldRender || !LDProvider) {
    return null;
  }

  return <LDProvider>{children}</LDProvider>;
};

interface AppProps {
  resetPasswordEmail?: string;
}

const App = ({ resetPasswordEmail }: AppProps) => {
  return (
    <div className="App">
      <ToastContainer position="top-center" />
      <React.Suspense fallback={<span>Loading...</span>}>
        <LDProviderWrapper>
          <Routes>
            <Route path="/" element={<Login resetPasswordEmail={resetPasswordEmail} />} />
            <Route path="/v2" element={<LegacyV2Redirect />} />
            <Route path="/password-reset" element={<CreatePassword />} />
            <Route
              path="/signup/checkout"
              element={
                <RequireSignup>
                  <Checkout />
                </RequireSignup>
              }
            />
            <Route
              path="/signup/verification"
              element={
                <RequireSignup>
                  <SmsVerification />
                </RequireSignup>
              }
            />
            <Route
              path="/signup"
              element={
                <RequirePricing>
                  <SignUp />
                </RequirePricing>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </LDProviderWrapper>
      </React.Suspense>
    </div>
  );
};

export default App;
